import { ChangeEvent, useCallback, useEffect, useState } from 'react';

import {
  alpha,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  makeStyles,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';

import HistoryMessagesSearch from 'components/HistoryMessagesSearch';
import ModalFinalizarAtendimento from 'components/ModalFinalizarAtendimento';
import TabPanel from 'components/TabPanel';
import { MediaTypeConfig } from 'constants/mediaTypes';
import DrawerDetalhesContatoSkeleton from 'pages/Contatos/components/DrawerDetalhesContatoSkeleton';
import InlineDrawerMidiasContato from 'pages/Contatos/components/InlineDrawerMidiasContato';
import TabPanelArquivos from 'pages/Contatos/components/TabPanelArquivos';
import TabPanelAtendimento from 'pages/Contatos/components/TabPanelAtendimento';
import TabPanelAtividadesContato from 'pages/Contatos/components/TabPanelAtividadesContato';
import TabPanelInformacoesContato from 'pages/Contatos/components/TabPanelInformacoesContato';
import { useAppDispatch, useAppSelector } from 'store';
import {
  AtendimentoInterface,
  AtendimentoInterfaceV2,
  AtendimentoFilaInterface,
  fetchFilaV2,
  finalizarAtendimentoNaFila,
  iniciarAtendimento,
  setAtendimentoSelecionado,
} from 'store/reducers/atendimentos';
import { fetchCanaisSelect } from 'store/reducers/canais';
import {
  ContatoInterfaceV2,
  contatosSelector,
  fetchContatoAtendimentoV2,
} from 'store/reducers/contatos';
import {
  navegarParaMensagem,
  resetAtendimentoFilaCursorAConsumir,
  resetIdMensagemPesquisada,
  resetMensagensAtendimentoNaFila,
  setCursorAConsumir,
  setIdMensagemPesquisada,
} from 'store/reducers/mensagens';
import { fetchTags } from 'store/reducers/tags';
import { getUrlParams } from 'utils/getUrlParams';

import RightGridMensagensAtendimento from './RightGridMensagensAtendimento';

const useStyles = makeStyles(
  ({ typography, spacing, palette, breakpoints }) => ({
    leftColumn: {
      height: '100%',
      maxWidth: 500,
      minWidth: '400px',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 1,
      position: 'relative',
      borderRight: `1px solid ${alpha(palette.divider, 0.07)}`,
      [breakpoints.down('md')]: {
        maxWidth: '100%',
        minWidth: '340px',
      },
      [breakpoints.down('sm')]: {
        minWidth: '250px',
      },
    },
    tab: {
      borderTop: `1px solid ${palette.divider}`,
      '& .MuiTab-root': {
        minWidth: 'auto',
        [breakpoints.down('sm')]: {
          minWidth: '4em',
          padding: spacing(1, 0.5),
          fontSize: typography.caption.fontSize,
        },
      },
    },
    tabPanel: {
      height: '100%',
      padding: spacing(3),
      overflowY: 'auto',
      overflowX: 'hidden',
      [breakpoints.down('sm')]: {
        padding: spacing(2),
      },
    },
    tabPanelHistorico: {
      height: '100%',
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    headerBox: {
      display: 'flex',
      flexDirection: 'column',
      gridGap: 24,
      padding: spacing(3, 3, 2),
      [breakpoints.down('sm')]: {
        padding: spacing(2, 2, 1.5),
        gridGap: 16,
      },
    },
  }),
);

interface LeftGridDetalhesContatoProps {
  atendimento:
    | AtendimentoInterface
    | AtendimentoInterfaceV2
    | AtendimentoFilaInterface;
  onClose: () => void;
  isSmallScreen?: boolean;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
}

export default function LeftGridDetalhesContato({
  atendimento,
  onClose,
  isSmallScreen = false,
  searchQuery = '',
  onSearchChange,
}: LeftGridDetalhesContatoProps) {
  const classes = useStyles();
  const theme = useTheme();
  const { orderBy } = getUrlParams();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();

  const contact =
    'contato' in atendimento ? atendimento.contato : atendimento.contact;

  const getContactId = () => {
    if (contact?.id) return contact.id;
    if ('contatoId' in atendimento)
      return (atendimento as AtendimentoInterface).contatoId;
    return '';
  };

  const contactId = getContactId();

  const contactName =
    contact && 'nome' in contact ? contact.nome : contact?.name;

  const channelCode =
    'channel' in atendimento
      ? (atendimento as AtendimentoInterfaceV2 | AtendimentoFilaInterface)
          .channel?.code || ''
      : '';

  const handleNavigateSearch = useCallback(
    async (messageId: string) => {
      try {
        const data = await dispatch(navegarParaMensagem(messageId)).unwrap();
        dispatch(resetAtendimentoFilaCursorAConsumir());
        dispatch(resetMensagensAtendimentoNaFila());
        dispatch(setIdMensagemPesquisada(messageId));
        dispatch(
          setCursorAConsumir({
            afterCursor: data.data.afterCursor,
            type: 'atendimento-na-fila',
          }),
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error navigating message:', error);
      }
    },
    [dispatch],
  );

  const handleClearSearch = useCallback(() => {
    dispatch(resetIdMensagemPesquisada());
  }, [dispatch]);

  const isContactBlocked = Boolean(
    (contact as { blockedAt?: string | null; bloqueadoEm?: string | null })
      ?.blockedAt ||
      (contact as { blockedAt?: string | null; bloqueadoEm?: string | null })
        ?.bloqueadoEm,
  );

  const {
    contatoAtendimentoV2: contato = {} as ContatoInterfaceV2,
    loadingContatoAtendimentoV2: loading,
  } = useAppSelector(contatosSelector);

  const [tabValue, setTabValue] = useState(0);
  const [showModalFinalizar, setShowModalFinalizar] = useState(false);

  // Estado do drawer inline de mídias (tab Arquivos)
  const [activeDrawer, setActiveDrawer] = useState<{
    mediaType: MediaTypeConfig;
    contatoId: string;
    canalId: string;
  } | null>(null);

  const handleOpenDrawer = useCallback(
    (params: {
      mediaType: MediaTypeConfig;
      contatoId: string;
      canalId: string;
    }) => {
      setActiveDrawer(params);
    },
    [],
  );

  const handleCloseDrawer = useCallback(() => {
    setActiveDrawer(null);
  }, []);

  const handleTabChange = (
    event: ChangeEvent<Record<string, unknown>>,
    newValue: number,
  ) => {
    setTabValue(newValue);
  };

  const handleClickIniciarAtendimento = () => {
    if (atendimento) {
      confirm({
        title: <Typography variant="body2">Iniciar atendimento</Typography>,
        description: (
          <Typography variant="caption">
            {`Deseja mesmo iniciar um novo atendimento com ${contactName}?`}
          </Typography>
        ),
        cancellationText: 'Cancelar',
        confirmationText: 'Iniciar',
        confirmationButtonProps: { variant: 'contained' },
        dialogProps: { maxWidth: 'xs' },
      })
        .then(() => {
          /**
           * Processos cruciais realizados após iniciar um novo atendimento da fila de espera
           *
           * 1º Seleciona o atendimento iniciado para o Chat
           * 2º Envia o atendente para a página do Chat
           * 3º Fecha o componente atual
           */
          dispatch(iniciarAtendimento(atendimento.id))
            .unwrap()
            .then((atendimentoSelecionado: AtendimentoInterface) => {
              dispatch(setAtendimentoSelecionado(atendimentoSelecionado));

              history.push('/');

              onClose();
            })
            .catch(error => {
              if (error.response) enqueueSnackbar(error.response.data.message);
            });
        })
        .catch(() => {
          //
        });
    }
  };

  const handleClickFinalizarAtendimentoNaFila = () => {
    if (atendimento) {
      setShowModalFinalizar(true);
    }
  };

  const handleConfirmFinalizar = (data: {
    atendimentoReasonId?: string;
    tagIds?: string[];
  }) => {
    if (atendimento) {
      dispatch(
        finalizarAtendimentoNaFila({ atendimentoId: atendimento.id, ...data }),
      )
        .unwrap()
        .then(() => {
          dispatch(fetchFilaV2({ orderBy }));

          enqueueSnackbar('O atendimento foi finalizado e removido da fila', {
            variant: 'success',
          });

          onClose();
          setShowModalFinalizar(false);
        })
        .catch(error => {
          if (error.response) enqueueSnackbar(error.response.data.message);
        });
    }
  };

  useEffect(() => {
    setTabValue(0);

    if (contactId) {
      dispatch(fetchContatoAtendimentoV2(contactId));

      dispatch(fetchCanaisSelect());
      dispatch(fetchTags());
    }
  }, [contactId, dispatch]);

  return (
    <Box className={classes.leftColumn}>
      <Box className={classes.headerBox}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="body1" style={{ fontWeight: 500 }}>
            Detalhes do atendimento na fila
          </Typography>
          {isSmallScreen && (
            <IconButton size="small" onClick={onClose} title="Fechar">
              <Close fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Grid container alignItems="center" spacing={1}>
          <Grid item xs={12} sm={6}>
            <Button
              fullWidth
              color="secondary"
              size={isSmallScreen ? 'small' : 'medium'}
              variant="outlined"
              onClick={handleClickFinalizarAtendimentoNaFila}
              style={{ whiteSpace: 'nowrap', borderRadius: 20 }}
            >
              Finalizar na fila
            </Button>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Tooltip
              title={
                isContactBlocked
                  ? 'Contato bloqueado. Não é possível iniciar atendimento.'
                  : ''
              }
              arrow
            >
              <span>
                <Button
                  fullWidth
                  color="primary"
                  size={isSmallScreen ? 'small' : 'medium'}
                  variant="contained"
                  onClick={handleClickIniciarAtendimento}
                  disabled={isContactBlocked}
                  style={{ whiteSpace: 'nowrap', borderRadius: 20 }}
                >
                  Iniciar atendimento
                </Button>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant="fullWidth"
        scrollButtons="auto"
        className={classes.tab}
      >
        <Tab label="Contato" onClick={() => setTabValue(0)} />
        {atendimento && (
          <Tab label="Atendimento" onClick={() => setTabValue(1)} />
        )}
        {isSmallScreen && (
          <Tab label="Histórico" onClick={() => setTabValue(2)} />
        )}
        <Tab
          label="Atividades"
          onClick={() => setTabValue(isSmallScreen ? 3 : 2)}
        />
        <Tab
          label="Arquivos"
          onClick={() => setTabValue(isSmallScreen ? 4 : 3)}
        />
      </Tabs>

      <Divider />

      <TabPanel value={tabValue} index={0} className={classes.tabPanel}>
        {loading && <DrawerDetalhesContatoSkeleton dense />}

        {!loading && (
          <TabPanelInformacoesContato
            contato={contato}
            readonlyFields
            isAtendimento={Boolean(atendimento)}
          />
        )}
      </TabPanel>

      {atendimento && (
        <TabPanel value={tabValue} index={1} className={classes.tabPanel}>
          <TabPanelAtendimento
            contatoId={contactId}
            atendimentoId={atendimento.id}
          />
        </TabPanel>
      )}

      <TabPanel
        value={tabValue}
        index={isSmallScreen ? 3 : 2}
        className={classes.tabPanel}
      >
        <TabPanelAtividadesContato
          contato={contato ?? ({} as ContatoInterfaceV2)}
        />
      </TabPanel>

      <TabPanel
        value={tabValue}
        index={isSmallScreen ? 4 : 3}
        className={classes.tabPanel}
      >
        <TabPanelArquivos
          contatoId={contactId}
          atendimento={atendimento as AtendimentoInterface}
          channelCode={channelCode}
          onOpenDrawer={handleOpenDrawer}
        />
      </TabPanel>

      {/* Drawer inline de mídias — sobrepõe o left grid, análogo ao ModalSearchDrawer */}
      {activeDrawer && (
        <InlineDrawerMidiasContato
          open={Boolean(activeDrawer)}
          onClose={handleCloseDrawer}
          contatoId={activeDrawer.contatoId}
          canalId={activeDrawer.canalId}
          mediaType={activeDrawer.mediaType}
        />
      )}

      {isSmallScreen && (
        <TabPanel
          value={tabValue}
          index={2}
          className={classes.tabPanelHistorico}
        >
          {channelCode && (
            <Box p={2} borderBottom={`1px solid ${theme.palette.divider}`}>
              <HistoryMessagesSearch
                contactId={contactId}
                channelCode={channelCode}
                onNavigate={handleNavigateSearch}
                onClear={handleClearSearch}
                onSearchChange={onSearchChange}
              />
            </Box>
          )}

          <Box flex={1} overflow="hidden">
            <RightGridMensagensAtendimento
              atendimentoId={atendimento.id}
              contactName={contactName || ''}
              searchQuery={searchQuery}
            />
          </Box>
        </TabPanel>
      )}

      <ModalFinalizarAtendimento
        open={showModalFinalizar}
        onClose={() => setShowModalFinalizar(false)}
        onConfirm={handleConfirmFinalizar}
        contactName={contactName || ''}
      />
    </Box>
  );
}
